import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Minus, Info } from 'lucide-react';
import scenarioApi, {
  ScenarioDefinition,
  YearlyPercentage,
  CreateTemporalDistributionRequest,
} from '../../api/scenarioApi';

interface TemporalDistributionsProps {
  darkMode: boolean;
  countries: { id: number; name: string; iso_code: string }[];
  selectedCountry: number | null;
  onCountryChange: (countryId: number | null) => void;
}

// Template types
type TemplateType = 'front_loaded' | 'back_loaded' | 'uniform' | 'bell_curve' | 'aggressive' | 'gradual';

const TemporalDistributions: React.FC<TemporalDistributionsProps> = ({ darkMode, countries: _countries, selectedCountry, onCountryChange: _onCountryChange }) => {
  const [scenarios, setScenarios] = useState<ScenarioDefinition[]>([]);
  const [yearlyPercentages, setYearlyPercentages] = useState<YearlyPercentage[]>([
    { year: 1, percentage: 16.67 },
    { year: 2, percentage: 16.67 },
    { year: 3, percentage: 16.67 },
    { year: 4, percentage: 16.67 },
    { year: 5, percentage: 16.67 },
    { year: 6, percentage: 16.65 },
  ]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | null>(null);
  const [activeTab, setActiveTab] = useState<'pattern' | 'application' | 'realization'>('pattern');
  const [selectedExampleScenario, setSelectedExampleScenario] = useState<'pessimistic' | 'realistic' | 'optimistic'>('realistic');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewDistribution, setIsNewDistribution] = useState(false);
  const [isUsingDefault, setIsUsingDefault] = useState(false);

  // Fetch scenarios on mount
  useEffect(() => {
    loadScenarios();
  }, []);

  // Load distribution when selected country changes
  useEffect(() => {
    if (selectedCountry) {
      loadDistribution(selectedCountry);
    }
  }, [selectedCountry]);

  const loadScenarios = async () => {
    try {
      const defs = await scenarioApi.getScenarioDefinitions(true);
      setScenarios(defs);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
    }
  };

  const loadDistribution = async (countryId: number) => {
    setError(null);
    setIsNewDistribution(false);
    setIsUsingDefault(false);
    try {
      const data = await scenarioApi.getTemporalDistribution(countryId);
      if (data) {
        setYearlyPercentages(data.yearly_percentages);
        setSelectedTemplate(data.template_name as TemplateType || null);
        setIsUsingDefault(false);
      } else {
        // No distribution exists yet - check if using defaults
        setIsNewDistribution(true);
        
        // Try to get the effective distribution to see if defaults are available
        try {
          const effectiveDist = await scenarioApi.getEffectiveTemporalDistribution(countryId, 'baseline');
          if (effectiveDist) {
            setYearlyPercentages(effectiveDist.yearly_percentages || []);
            setIsUsingDefault(effectiveDist.distribution_source === 'default');
          }
        } catch (effectiveErr) {
          console.log('Using fallback distribution');
          setIsUsingDefault(true);
        }
      }
    } catch (err: any) {
      // The scenarioApi should handle 404s and return null
      // If we're here, it's likely a different error
      console.error('Error loading temporal distribution:', err);
      
      // For now, just treat it as if no distribution exists
      // This allows the UI to work even if the backend is having issues
      setIsNewDistribution(true);
      setIsUsingDefault(true);
    }
  };

  const handleSave = async () => {
    if (!selectedCountry) return;

    setSaving(true);
    setError(null);
    try {
      const request: CreateTemporalDistributionRequest = {
        country_id: selectedCountry,
        yearly_percentages: yearlyPercentages,
        template_name: selectedTemplate || undefined,
      };

      await scenarioApi.createOrUpdateTemporalDistribution(request);
      setIsNewDistribution(false); // Not new anymore after successful save
      setIsUsingDefault(false); // No longer using defaults after saving
      await loadDistribution(selectedCountry);
    } catch (err) {
      console.error('Failed to save distribution:', err);
      setError('Failed to save distribution');
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (template: TemplateType) => {
    setSelectedTemplate(template);
    let newPercentages: YearlyPercentage[] = [];

    switch (template) {
      case 'front_loaded':
        newPercentages = [
          { year: 1, percentage: 30 },
          { year: 2, percentage: 25 },
          { year: 3, percentage: 20 },
          { year: 4, percentage: 15 },
          { year: 5, percentage: 7 },
          { year: 6, percentage: 3 },
        ];
        break;
      case 'back_loaded':
        newPercentages = [
          { year: 1, percentage: 3 },
          { year: 2, percentage: 7 },
          { year: 3, percentage: 15 },
          { year: 4, percentage: 20 },
          { year: 5, percentage: 25 },
          { year: 6, percentage: 30 },
        ];
        break;
      case 'uniform':
        const years = yearlyPercentages.length;
        const percentage = 100 / years;
        newPercentages = yearlyPercentages.map((_yp, i) => ({ year: i + 1, percentage }));
        break;
      case 'bell_curve':
        newPercentages = [
          { year: 1, percentage: 10 },
          { year: 2, percentage: 20 },
          { year: 3, percentage: 25 },
          { year: 4, percentage: 25 },
          { year: 5, percentage: 15 },
          { year: 6, percentage: 5 },
        ];
        break;
      case 'aggressive':
        newPercentages = [
          { year: 1, percentage: 35 },
          { year: 2, percentage: 25 },
          { year: 3, percentage: 18 },
          { year: 4, percentage: 12 },
          { year: 5, percentage: 7 },
          { year: 6, percentage: 3 },
        ];
        break;
      case 'gradual':
        newPercentages = [
          { year: 1, percentage: 5 },
          { year: 2, percentage: 10 },
          { year: 3, percentage: 15 },
          { year: 4, percentage: 20 },
          { year: 5, percentage: 23 },
          { year: 6, percentage: 27 },
        ];
        break;
    }

    setYearlyPercentages(newPercentages);
  };

  const addYear = () => {
    const newYear = yearlyPercentages.length + 1;
    const redistributed = redistributePercentages([...yearlyPercentages, { year: newYear, percentage: 0 }]);
    setYearlyPercentages(redistributed);
    setSelectedTemplate(null);
  };

  const removeYear = () => {
    if (yearlyPercentages.length > 1) {
      const redistributed = redistributePercentages(yearlyPercentages.slice(0, -1));
      setYearlyPercentages(redistributed);
      setSelectedTemplate(null);
    }
  };

  const redistributePercentages = (percentages: YearlyPercentage[]): YearlyPercentage[] => {
    const equalPercentage = 100 / percentages.length;
    return percentages.map((_yp, i) => ({ year: i + 1, percentage: equalPercentage }));
  };

  const updateYearPercentage = (yearIndex: number, value: number) => {
    const newPercentages = [...yearlyPercentages];
    newPercentages[yearIndex].percentage = Math.max(0, Math.min(100, value));
    setYearlyPercentages(newPercentages);
    setSelectedTemplate(null);
  };

  const totalPercentage = useMemo(() => {
    return yearlyPercentages.reduce((sum, yp) => sum + yp.percentage, 0);
  }, [yearlyPercentages]);

  const cumulativePercentages = useMemo(() => {
    let cumulative = 0;
    return yearlyPercentages.map(yp => {
      cumulative += yp.percentage;
      return cumulative;
    });
  }, [yearlyPercentages]);

  // Example scenario calculations
  const exampleScenario = useMemo(() => {
    const scenario = scenarios.find(s => {
      if (selectedExampleScenario === 'pessimistic') return s.type === 'pessimistic';
      if (selectedExampleScenario === 'realistic') return s.type === 'baseline';
      if (selectedExampleScenario === 'optimistic') return s.type === 'optimistic';
      return false;
    });

    if (!scenario) return null;

    const baseImprovement = scenario.default_percentage_adjustment || 10;
    return yearlyPercentages.map(yp => ({
      year: yp.year,
      value: (baseImprovement * yp.percentage) / 100,
    }));
  }, [selectedExampleScenario, scenarios, yearlyPercentages]);

  return (
    <div className={`p-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Temporal Distribution Pattern</h1>
        <p className="text-gray-500">
          Define how scenario benefits are realized over time. The distribution curve will apply to all metrics for the country specified.
        </p>
        
        {/* Indicator for default vs custom distribution */}
        {selectedCountry && (
          <div className={`mt-4 px-4 py-2 rounded-lg inline-flex items-center gap-2 ${
            isUsingDefault 
              ? darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
              : darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
          }`}>
            <Info className="w-4 h-4" />
            <span className="text-sm font-medium">
              {isUsingDefault 
                ? 'Using default temporal distribution' 
                : 'Using country-specific temporal distribution'}
            </span>
            {isUsingDefault && isNewDistribution && (
              <span className="text-xs opacity-75">
                (Save to create country-specific configuration)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Distribution Configuration */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Distribution Pattern Configuration</h2>
          <div className="flex gap-2">
            <button
              onClick={removeYear}
              disabled={yearlyPercentages.length <= 1}
              className={`
                px-4 py-2 rounded-lg flex items-center gap-2 transition-colors
                ${darkMode 
                  ? 'bg-gray-800 hover:bg-gray-700 disabled:bg-gray-800 disabled:opacity-50' 
                  : 'bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 disabled:opacity-50'}
              `}
            >
              <Minus className="w-4 h-4" />
              Remove Year
            </button>
            <button
              onClick={addYear}
              className={`
                px-4 py-2 rounded-lg flex items-center gap-2 transition-colors
                ${darkMode 
                  ? 'bg-gray-800 hover:bg-gray-700' 
                  : 'bg-gray-100 hover:bg-gray-200'}
              `}
            >
              <Plus className="w-4 h-4" />
              Add Year
            </button>
          </div>
        </div>

        {/* Quick Templates */}
        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-3">Quick Templates:</p>
          <div className="grid grid-cols-3 gap-3">
            {(['front_loaded', 'back_loaded', 'uniform', 'bell_curve', 'aggressive', 'gradual'] as TemplateType[]).map(template => (
              <button
                key={template}
                onClick={() => applyTemplate(template)}
                className={`
                  px-4 py-2 rounded-lg capitalize transition-colors
                  ${selectedTemplate === template
                    ? darkMode 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-indigo-500 text-white'
                    : darkMode 
                      ? 'bg-gray-800 hover:bg-gray-700' 
                      : 'bg-gray-100 hover:bg-gray-200'}
                `}
              >
                {template.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Year Inputs */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {yearlyPercentages.map((yp, index) => (
            <div key={index} className="flex items-center gap-3">
              <label className="font-medium w-20">Year {yp.year}</label>
              <div className="flex-1 flex items-center gap-2">
                <input
                  type="number"
                  value={yp.percentage}
                  onChange={(e) => updateYearPercentage(index, parseFloat(e.target.value) || 0)}
                  className={`
                    flex-1 px-3 py-2 rounded-lg border
                    ${darkMode 
                      ? 'bg-gray-800 border-gray-700' 
                      : 'bg-white border-gray-300'}
                  `}
                />
                <span className="text-gray-500">%</span>
              </div>
              <div className="w-32">
                <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
                  <div 
                    className="h-full rounded-full bg-indigo-500"
                    style={{ width: `${yp.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total Distribution Bar */}
        <div className="mb-4">
          <div className="flex justify-between mb-2">
            <span className="font-medium">Total Distribution:</span>
            <span className={`font-bold ${Math.abs(totalPercentage - 100) < 0.01 ? 'text-emerald-400' : 'text-red-400'}`}>
              {totalPercentage.toFixed(1)}%
            </span>
          </div>
          <div className={`h-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div 
              className={`h-full rounded-lg ${Math.abs(totalPercentage - 100) < 0.01 ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${Math.min(100, totalPercentage)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex gap-4 border-b">
          {['pattern', 'application', 'realization'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`
                pb-2 px-4 capitalize transition-colors border-b-2
                ${activeTab === tab
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent hover:border-gray-300'}
              `}
            >
              {tab === 'pattern' ? 'Distribution Pattern' : 
               tab === 'application' ? 'Scenario Application' : 
               'Cumulative Realization'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mb-8">
        {activeTab === 'pattern' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Temporal Distribution Pattern</h3>
            {/* Chart visualization */}
            <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="relative h-80">
                {/* Y-axis labels and grid lines */}
                <div className="absolute left-0 top-0 bottom-12 w-12 flex flex-col justify-between">
                  {[36, 27, 18, 9, 0].map((value) => (
                    <div key={value} className="flex items-center">
                      <span className="text-xs text-gray-500 w-8 text-right">{value}</span>
                    </div>
                  ))}
                </div>
                
                {/* Grid lines */}
                <div className="absolute left-12 right-0 top-0 bottom-12">
                  {[36, 27, 18, 9, 0].map((value) => (
                    <div 
                      key={value} 
                      className={`absolute w-full border-t ${darkMode ? 'border-gray-700' : 'border-gray-300'} ${value === 0 ? 'border-gray-500' : 'border-dashed'}`}
                      style={{ top: `${((36 - value) / 36) * 100}%` }}
                    />
                  ))}
                </div>
                
                {/* Chart area */}
                <div className="absolute left-12 right-0 top-0 bottom-12 flex items-end justify-evenly">
                  {yearlyPercentages.map((yp, i) => (
                    <div key={i} className="flex justify-center items-end h-full" style={{ width: '60px' }}>
                      <div
                        className="bg-indigo-500 rounded-t transition-all duration-300 hover:opacity-80"
                        style={{ 
                          width: '100%',
                          height: `${Math.max(1, (yp.percentage / 36) * 100)}%`,
                          minHeight: '2px'
                        }}
                      />
                    </div>
                  ))}
                </div>
                
                {/* X-axis labels */}
                <div className="absolute left-12 right-0 bottom-0 flex justify-evenly">
                  {yearlyPercentages.map((yp) => (
                    <div key={yp.year} style={{ width: '60px' }} className="text-center">
                      <span className="text-sm text-gray-500">{yp.year}</span>
                    </div>
                  ))}
                </div>
                
                {/* Y-axis label */}
                <div className="absolute -left-8 top-1/2 -rotate-90 origin-center">
                  <span className="text-xs text-gray-500">Distribution (%)</span>
                </div>
                
                {/* X-axis label */}
                <div className="absolute bottom-0 left-0 right-0 text-center mt-4">
                  <span className="text-xs text-gray-500">Year</span>
                </div>
              </div>
              
              {/* Description below chart */}
              <p className="text-sm text-gray-500 text-center mt-6">
                This pattern determines how benefits are distributed across time periods
              </p>
            </div>
          </div>
        )}

        {activeTab === 'application' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Example: How Distribution Applies to Scenarios</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">Select scenario for example:</p>
              <div className="flex gap-2">
                {[
                  { key: 'pessimistic', label: 'Pessimistic (5%)' },
                  { key: 'realistic', label: 'Baseline (10%)' },
                  { key: 'optimistic', label: 'Optimistic (15%)' },
                ].map(option => (
                  <button
                    key={option.key}
                    onClick={() => setSelectedExampleScenario(option.key as any)}
                    className={`
                      px-4 py-2 rounded-lg transition-colors
                      ${selectedExampleScenario === option.key
                        ? 'bg-indigo-500 text-white'
                        : darkMode 
                          ? 'bg-gray-800 hover:bg-gray-700' 
                          : 'bg-gray-100 hover:bg-gray-200'}
                    `}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Example chart */}
            <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="relative h-80">
                {/* Dynamic Y-axis based on selected scenario */}
                {(() => {
                  const maxValue = selectedExampleScenario === 'pessimistic' ? 1.5 : 
                                  selectedExampleScenario === 'realistic' ? 2.5 : 4.0;
                  const yAxisValues = selectedExampleScenario === 'pessimistic' ? 
                    [1.5, 1.125, 0.75, 0.375, 0] :
                    selectedExampleScenario === 'realistic' ? 
                    [2.5, 1.875, 1.25, 0.625, 0] :
                    [4.0, 3.0, 2.0, 1.0, 0];
                  
                  return (
                    <>
                      {/* Y-axis labels and grid lines */}
                      <div className="absolute left-0 top-0 bottom-12 w-16 flex flex-col justify-between">
                        {yAxisValues.map((value) => (
                          <div key={value} className="flex items-center">
                            <span className="text-xs text-gray-500 w-12 text-right">
                              {value % 1 === 0 ? value.toFixed(1) : value.toFixed(3).replace(/\.?0+$/, '')}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Grid lines */}
                      <div className="absolute left-16 right-0 top-0 bottom-12">
                        {yAxisValues.map((value) => (
                          <div 
                            key={value} 
                            className={`absolute w-full border-t ${darkMode ? 'border-gray-700' : 'border-gray-300'} ${value === 0 ? 'border-gray-500' : 'border-dashed'}`}
                            style={{ top: `${((maxValue - value) / maxValue) * 100}%` }}
                          />
                        ))}
                      </div>
                      
                      {/* Chart area with smooth curve */}
                      <div className="absolute left-16 right-0 top-0 bottom-12">
                        {exampleScenario && exampleScenario.length > 0 && (
                          <>
                            {/* SVG for line and area */}
                            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {(() => {
                                // Calculate positions in viewBox coordinates (0-100)
                                const points = exampleScenario.map((point, i) => ({
                                  x: ((i + 0.5) / exampleScenario.length) * 100,
                                  y: 100 - (point.value / maxValue) * 100
                                }));
                                
                                // Create smooth curve path
                                let pathData = `M ${points[0].x} ${points[0].y}`;
                                
                                for (let i = 0; i < points.length - 1; i++) {
                                  const cp1x = points[i].x + (points[i + 1].x - points[i].x) * 0.5;
                                  const cp1y = points[i].y;
                                  const cp2x = points[i + 1].x - (points[i + 1].x - points[i].x) * 0.5;
                                  const cp2y = points[i + 1].y;
                                  
                                  pathData += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${points[i + 1].x} ${points[i + 1].y}`;
                                }
                                
                                return (
                                  <>
                                    {/* Area fill */}
                                    <path
                                      d={`${pathData} L ${points[points.length - 1].x} 100 L ${points[0].x} 100 Z`}
                                      fill="rgba(239, 68, 68, 0.3)"
                                      className="transition-all duration-300"
                                    />
                                    
                                    {/* Line */}
                                    <path
                                      d={pathData}
                                      fill="none"
                                      stroke="rgb(239, 68, 68)"
                                      strokeWidth="0.5"
                                      className="transition-all duration-300"
                                      vectorEffect="non-scaling-stroke"
                                    />
                                  </>
                                );
                              })()}
                            </svg>
                            
                            {/* Points as overlaid elements */}
                            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {exampleScenario.map((point, i) => {
                                const x = ((i + 0.5) / exampleScenario.length) * 100;
                                const y = 100 - (point.value / maxValue) * 100;
                                return (
                                  <circle
                                    key={i}
                                    cx={x}
                                    cy={y}
                                    r="0.8"
                                    fill="white"
                                    stroke="rgb(239, 68, 68)"
                                    strokeWidth="0.3"
                                    vectorEffect="non-scaling-stroke"
                                  />
                                );
                              })}
                            </svg>
                          </>
                        )}
                      </div>
                    </>
                  );
                })()}
                
                {/* X-axis labels */}
                <div className="absolute left-16 right-0 bottom-0 flex justify-evenly">
                  {exampleScenario?.map((point) => (
                    <div key={point.year} className="text-center" style={{ width: '60px' }}>
                      <span className="text-sm text-gray-500">{point.year}</span>
                    </div>
                  ))}
                </div>
                
                {/* Y-axis label - more visible */}
                <div className="absolute -left-2 top-1/2 -rotate-90" style={{ transformOrigin: 'center' }}>
                  <span className="text-sm font-medium text-gray-700">Percentage of Scenario</span>
                </div>
                
                {/* X-axis label */}
                <div className="absolute bottom-0 left-0 right-0 text-center">
                  <span className="text-xs text-gray-500">Year</span>
                </div>
              </div>
              
              <p className="mt-4 text-sm text-blue-600">
                Example: With the {selectedExampleScenario === 'pessimistic' ? 'pessimistic' : selectedExampleScenario === 'realistic' ? 'baseline' : 'optimistic'} scenario ({selectedExampleScenario === 'pessimistic' ? '5' : selectedExampleScenario === 'realistic' ? '10' : '15'}% improvement), Year 1 would realize {yearlyPercentages[0]?.percentage.toFixed(0)}% 
                of that improvement, which equals {((selectedExampleScenario === 'pessimistic' ? 5 : selectedExampleScenario === 'realistic' ? 10 : 15) * yearlyPercentages[0]?.percentage / 100).toFixed(2)}% of the base metric value.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'realization' && (
          <div>
            {/* Summary Table */}
            <h2 className="text-xl font-semibold mb-4">Distribution Summary</h2>
            <div className={`rounded-lg overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-200'}>
                    <th className="px-4 py-3 text-left">YEAR</th>
                    <th className="px-4 py-3 text-center">DISTRIBUTION</th>
                    <th className="px-4 py-3 text-left">VISUAL</th>
                    <th className="px-4 py-3 text-right">CUMULATIVE</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyPercentages.map((yp, i) => (
                    <tr key={i} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="px-4 py-3">Year {yp.year}</td>
                      <td className="px-4 py-3 text-center font-bold text-indigo-400">
                        {yp.percentage.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3">
                        <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} w-32`}>
                          <div 
                            className="h-full rounded-full bg-indigo-500"
                            style={{ width: `${yp.percentage}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {cumulativePercentages[i].toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                  <tr className={`border-t-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                    <td className="px-4 py-3 font-bold">Total</td>
                    <td className="px-4 py-3 text-center font-bold text-emerald-400">
                      {totalPercentage.toFixed(1)}%
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                      {Math.abs(totalPercentage - 100) < 0.01 && <span className="text-emerald-400">✓</span>}
                      <span className="text-gray-500">Properly normalized</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-amber-50'} mb-6`}>
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-2">How This Works in Your System</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• This distribution pattern will be applied to <strong>all metrics</strong> for the specified country</li>
              <li>• Each scenario (pessimistic, realistic, optimistic) will use the same temporal distribution</li>
              <li>• The percentages determine how much of each scenario's impact is realized per year</li>
              <li>• Changes will take effect immediately after saving</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        {Math.abs(totalPercentage - 100) > 0.01 && (
          <p className="text-red-500 py-2">
            Total must equal 100%. Currently: {totalPercentage.toFixed(1)}%
          </p>
        )}
        <button
          onClick={handleSave}
          disabled={Math.abs(totalPercentage - 100) > 0.01 || saving}
          className={`
            px-6 py-2 rounded-lg font-medium transition-colors
            ${Math.abs(totalPercentage - 100) > 0.01 || saving
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-indigo-500 hover:bg-indigo-600 text-white'}
          `}
        >
          {saving ? 'Saving...' : 'Save Distribution'}
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500">
          {error}
        </div>
      )}
      
      {isNewDistribution && !error && (
        <div className="mt-4 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
          <div className="flex items-start gap-2">
            <span className="text-lg">ℹ️</span>
            <div>
              <div className="font-medium">No distribution configured yet</div>
              <div className="text-sm opacity-80 mt-1">
                This country doesn't have a temporal distribution configured. 
                The default uniform distribution is shown. Adjust and save to set your preferred distribution.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemporalDistributions;